import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  message,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import dayjs, { type Dayjs } from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useMemo, useState } from 'react';

import ParentLink from '../ClubRegistration/ParentLink';
import type { ClubPeriod, ParentDetails } from '../ClubRegistration/types';
import { formatMNT, formatPeriod, queryErrorMessage } from '../ClubRegistration/types';

const { Text } = Typography;

// One row of club_billing_preview: a Club month the daily job bills, or why
// it can't (docs/billing.md §5).
interface BillingRow extends ClubPeriod {
  club_student_id: number;
  club_name: string;
  student_name: string;
  class_name: string | null;
  card_balance: number;
  reason: 'NO_SESSIONS' | 'NO_SESSIONS_LEFT' | 'NOT_SET_UP' | null;
  club_month_id?: number;
  full_amount?: number;
  discount_amount?: number;
  amount?: number;
}

interface DebtorInvoice extends ClubPeriod {
  id: number;
  club_id: number;
  club_name: string;
  amount: number;
  created_at: string | null;
}

interface DebtorRow {
  student_id: number;
  student_name: string;
  class_id: number | null;
  class_name: string | null;
  clubs: string;
  club_ids: number[];
  parent_name: string | null;
  parent: ParentDetails | null;
  card_balance: number;
  outstanding_amount: number;
  invoices_count: number;
  oldest_period_start: string | null;
  oldest_created_at: string | null;
  invoices: DebtorInvoice[];
}

// "Волейбол шигшээ - Д. Анхбаяр" → "Волейбол шигшээ": the teacher is noise in a list.
const shortClubName = (name: string) => name.split(' - ')[0].trim() || name;

// Days since the oldest Outstanding payment's Club month began (or it was made).
const debtSince = (row: DebtorRow) => row.oldest_period_start ?? row.oldest_created_at;
const debtDays = (row: DebtorRow) => {
  const since = debtSince(row);
  return since ? Math.max(0, dayjs().startOf('day').diff(dayjs(since).startOf('day'), 'day')) : 0;
};

// What a recharge must bring for the card balance to pay everything owed.
const toRecharge = (row: DebtorRow) =>
  Math.max(0, row.outstanding_amount - Math.max(0, row.card_balance));

const LONG_OVERDUE_DAYS = 30;

const REASON: Record<string, { label: string; color: string }> = {
  NO_SESSIONS: { label: 'Хичээлийн өдөргүй сар', color: 'red' },
  NO_SESSIONS_LEFT: { label: 'Үлдсэн өдөр алга', color: 'default' },
  NOT_SET_UP: { label: 'Шилжүүлээгүй гишүүнчлэл', color: 'red' },
};

const billingColumns: ColumnsType<BillingRow> = [
  {
    title: 'Дугуйлан',
    dataIndex: 'club_name',
    render: (v: string) => <Tooltip title={v}>{shortClubName(v)}</Tooltip>,
  },
  { title: 'Сурагч', dataIndex: 'student_name' },
  { title: 'Анги', dataIndex: 'class_name', render: (v) => v ?? '—' },
  {
    title: 'Хугацаа',
    key: 'period',
    render: (_, row) =>
      row.reason ? (
        <Tag color={REASON[row.reason]?.color}>{REASON[row.reason]?.label ?? row.reason}</Tag>
      ) : (
        formatPeriod(row)
      ),
  },
  {
    title: 'Дүн',
    dataIndex: 'amount',
    align: 'right',
    render: (v: number | undefined, row) =>
      v == null ? (
        '—'
      ) : (
        <>
          {formatMNT(v)}
          {(row.discount_amount ?? 0) > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {' '}
              (−{formatMNT(row.discount_amount ?? 0)})
            </Text>
          )}
        </>
      ),
  },
  {
    title: 'Картын үлдэгдэл',
    dataIndex: 'card_balance',
    align: 'right',
    render: (v: number, row) =>
      row.amount != null && v < row.amount ? (
        <Text type="warning">{formatMNT(v)}</Text>
      ) : (
        formatMNT(v)
      ),
  },
];

const debtorColumns: ColumnsType<DebtorRow> = [
  {
    title: 'Сурагч',
    dataIndex: 'student_name',
    sorter: (a, b) => a.student_name.localeCompare(b.student_name, 'mn'),
    render: (v: string, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {row.class_name ?? '—'}
        </Text>
      </div>
    ),
  },
  {
    title: 'Дугуйлан',
    key: 'clubs',
    render: (_, row) => {
      const names = [...new Set(row.invoices.map((i) => i.club_name))];
      const shown = names.slice(0, 2);
      return (
        <Space size={[4, 4]} wrap>
          {shown.map((n) => (
            <Tooltip key={n} title={n}>
              <Tag style={{ marginInlineEnd: 0 }}>{shortClubName(n)}</Tag>
            </Tooltip>
          ))}
          {names.length > shown.length && (
            <Tooltip title={names.slice(2).map(shortClubName).join(', ')}>
              <Tag style={{ marginInlineEnd: 0 }}>+{names.length - shown.length}</Tag>
            </Tooltip>
          )}
        </Space>
      );
    },
  },
  {
    title: 'Эцэг эх',
    dataIndex: 'parent_name',
    render: (v, row) => (
      <div>
        <ParentLink name={v} parent={row.parent} />
        <div>
          {row.parent?.phone ? (
            <Text copyable={{ text: row.parent.phone }} style={{ fontSize: 12 }}>
              {row.parent.phone}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Утасгүй
            </Text>
          )}
        </div>
      </div>
    ),
  },
  {
    title: 'Төлөгдөөгүй',
    dataIndex: 'outstanding_amount',
    align: 'right',
    sorter: (a, b) => a.outstanding_amount - b.outstanding_amount,
    render: (v: number, row) => (
      <div>
        <Text strong>{formatMNT(v)}</Text>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.invoices_count} нэхэмжлэл
          </Text>
        </div>
      </div>
    ),
  },
  {
    title: 'Картын үлдэгдэл',
    dataIndex: 'card_balance',
    align: 'right',
    sorter: (a, b) => a.card_balance - b.card_balance,
    render: (v: number, row) => (
      <div>
        <Text type={v < 0 ? 'danger' : undefined}>{formatMNT(v)}</Text>
        {toRecharge(row) > 0 && (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Цэнэглэх: {formatMNT(toRecharge(row))}
            </Text>
          </div>
        )}
      </div>
    ),
  },
  {
    title: 'Хугацаа',
    key: 'since',
    align: 'right',
    defaultSortOrder: 'descend',
    sorter: (a, b) => debtDays(a) - debtDays(b),
    render: (_, row) => {
      const days = debtDays(row);
      const since = debtSince(row);
      return (
        <Tooltip title={since ? `${dayjs(since).format('YYYY-MM-DD')}-наас` : undefined}>
          <Tag
            color={days > LONG_OVERDUE_DAYS ? 'red' : days > 15 ? 'orange' : 'default'}
            style={{ marginInlineEnd: 0 }}
          >
            {days} хоног
          </Tag>
        </Tooltip>
      );
    },
  },
];

// A debtor's Outstanding payments, one line each.
const DebtorInvoices = ({ row }: { row: DebtorRow }) => (
  <Table<DebtorInvoice>
    size="small"
    rowKey="id"
    pagination={false}
    dataSource={row.invoices}
    columns={[
      { title: 'Дугуйлан', dataIndex: 'club_name' },
      {
        title: 'Хугацаа',
        key: 'period',
        render: (_, inv) =>
          inv.period_start ? formatPeriod(inv) : <Text type="secondary">Хуучин нэхэмжлэл</Text>,
      },
      {
        title: 'Дүн',
        dataIndex: 'amount',
        align: 'right',
        render: (v: number) => formatMNT(v),
      },
    ]}
  />
);

function BillingTab({ canEdit }: { canEdit: boolean }) {
  const { runQuery } = useScreen();
  const [onDate, setOnDate] = useState<Dayjs>(() => dayjs());
  const [rows, setRows] = useState<BillingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    runQuery<BillingRow>('club_billing_preview', { onDate: onDate.format('YYYY-MM-DD') })
      .then(setRows)
      .catch((e) => setError(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [onDate, runQuery]);

  useEffect(() => {
    load();
  }, [load]);

  const billed = rows.filter((r) => !r.reason);
  const total = billed.reduce((sum, r) => sum + (r.amount ?? 0), 0);
  const covered = billed.filter((r) => r.card_balance >= (r.amount ?? 0)).length;
  const problems = rows.filter((r) => r.reason === 'NO_SESSIONS' || r.reason === 'NOT_SET_UP');
  const isToday = onDate.isSame(dayjs(), 'day');

  const run = () => {
    setRunning(true);
    runQuery<BillingRow>('club_billing_run')
      .then((created) => {
        message.success(`${created.length} нэхэмжлэл үүслээ.`);
        load();
      })
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setRunning(false));
  };

  return (
    <>
      <Space wrap style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <Text type="secondary">Өдөр</Text>
          <DatePicker value={onDate} allowClear={false} onChange={(v) => v && setOnDate(v)} />
        </Space>
        {canEdit && (
          <Popconfirm
            title="Одоо нэхэмжлэх үү?"
            description={`${billed.length} нэхэмжлэл (${formatMNT(total)}) үүсч, үлдэгдэл хүрэлцсэнийг нь төлнө.`}
            okText="Нэхэмжлэх"
            cancelText="Болих"
            onConfirm={run}
            disabled={!isToday || billed.length === 0}
          >
            <Button
              type="primary"
              loading={running}
              disabled={!isToday || billed.length === 0 || loading}
            >
              Одоо нэхэмжлэх
            </Button>
          </Popconfirm>
        )}
      </Space>

      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Өдөр бүрийн автомат нэхэмжлэл энэ өдөр юу үүсгэхийг харуулна. Юу ч бичихгүй. "Одоо
        нэхэмжлэх" нь зөвхөн өнөөдрийнхийг үүсгэнэ.
      </Text>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      <Space size="large" wrap style={{ marginBottom: 16 }}>
        <Statistic title="Нэхэмжлэл" value={billed.length} />
        <Statistic title="Нийт дүн" value={formatMNT(total)} />
        <Statistic title="Үлдэгдэл хүрэлцэх" value={`${covered} / ${billed.length}`} />
      </Space>

      {problems.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${problems.length} мөр шалгах шаардлагатай: хичээлийн өдөргүй сар эсвэл шилжүүлээгүй гишүүнчлэл.`}
        />
      )}

      {!loading && rows.length === 0 ? (
        <Empty description="Энэ өдөр нэхэмжлэх зүйл алга" />
      ) : (
        <Table
          rowKey={(r) => `${r.club_student_id}-${r.club_month_id ?? r.reason}`}
          loading={loading}
          columns={billingColumns}
          dataSource={rows}
          pagination={{
            pageSize: 15,
            hideOnSinglePage: true,
            showSizeChanger: false,
            showTotal: (total, [from, to]) => `${from}–${to} / ${total}`,
          }}
        />
      )}
    </>
  );
}

function DebtorsTab() {
  const { runQuery } = useScreen();
  const [rows, setRows] = useState<DebtorRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [clubId, setClubId] = useState<number>();
  const [className, setClassName] = useState<string>();
  const [age, setAge] = useState<'all' | 'long'>('all');

  useEffect(() => {
    setLoading(true);
    runQuery<DebtorRow>('club_debtors')
      .then(setRows)
      .catch((e) => setError(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [runQuery]);

  const clubOptions = useMemo(() => {
    const byId = new Map<number, string>();
    for (const r of rows) for (const i of r.invoices) byId.set(i.club_id, i.club_name);
    return [...byId.entries()]
      .map(([value, name]) => ({ value, label: shortClubName(name) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'mn'));
  }, [rows]);
  const classOptions = useMemo(
    () =>
      [...new Set(rows.map((r) => r.class_name).filter((c): c is string => !!c))]
        .sort((a, b) => a.localeCompare(b, 'mn', { numeric: true }))
        .map((c) => ({ value: c, label: c })),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!q ||
          r.student_name.toLowerCase().includes(q) ||
          (r.parent_name ?? '').toLowerCase().includes(q) ||
          (r.parent?.phone ?? '').includes(q)) &&
        (!clubId || r.club_ids.includes(clubId)) &&
        (!className || r.class_name === className) &&
        (age === 'all' || debtDays(r) > LONG_OVERDUE_DAYS),
    );
  }, [rows, search, clubId, className, age]);

  const total = filtered.reduce((sum, r) => sum + r.outstanding_amount, 0);
  const longOverdue = filtered.filter((r) => debtDays(r) > LONG_OVERDUE_DAYS);
  const rechargeTotal = filtered.reduce((sum, r) => sum + toRecharge(r), 0);
  const noPhone = filtered.filter((r) => !r.parent?.phone).length;

  return (
    <>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Өртэй сурагч" value={filtered.length} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Нийт төлөгдөөгүй" value={formatMNT(total)} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title={`${LONG_OVERDUE_DAYS}+ хоног`}
              value={longOverdue.length}
              suffix={
                <Text type="secondary" style={{ fontSize: 14 }}>
                  · {formatMNT(longOverdue.reduce((s, r) => s + r.outstanding_amount, 0))}
                </Text>
              }
              valueStyle={longOverdue.length ? { color: '#cf1322' } : undefined}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Tooltip title="Карт цэнэглэгдвэл автоматаар төлөгдөх дүн (сөрөг үлдэгдэлтэй бол түүнийг ч нөхнө)">
              <Statistic title="Цэнэглэх шаардлагатай" value={formatMNT(rechargeTotal)} />
            </Tooltip>
          </Card>
        </Col>
      </Row>

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          placeholder="Сурагч, эцэг эх, утас"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 240 }}
        />
        <Select
          placeholder="Дугуйлан"
          allowClear
          showSearch
          optionFilterProp="label"
          value={clubId}
          onChange={setClubId}
          options={clubOptions}
          style={{ width: 200 }}
        />
        <Select
          placeholder="Анги"
          allowClear
          showSearch
          value={className}
          onChange={setClassName}
          options={classOptions}
          style={{ width: 120 }}
        />
        <Segmented
          value={age}
          onChange={(v) => setAge(v as 'all' | 'long')}
          options={[
            { value: 'all', label: 'Бүгд' },
            { value: 'long', label: `${LONG_OVERDUE_DAYS}+ хоног` },
          ]}
        />
        {noPhone > 0 && (
          <Text type="warning" style={{ fontSize: 12 }}>
            {noPhone} сурагчийн эцэг эхийн утас бүртгэлгүй
          </Text>
        )}
      </Space>

      <Table
        rowKey="student_id"
        size="middle"
        loading={loading}
        columns={debtorColumns}
        dataSource={filtered}
        expandable={{
          expandedRowRender: (row) => <DebtorInvoices row={row} />,
          expandRowByClick: true,
        }}
        locale={{ emptyText: <Empty description="Өртэй сурагч алга" /> }}
        pagination={{
          pageSize: 15,
          hideOnSinglePage: true,
          showSizeChanger: false,
          showTotal: (total, [from, to]) => `${from}–${to} / ${total}`,
        }}
        scroll={{ x: 900 }}
      />
    </>
  );
}

const ClubBilling = () => {
  const { level } = useScreen();
  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <Tabs
          items={[
            { key: 'debtors', label: 'Өртэй сурагчид', children: <DebtorsTab /> },
            {
              key: 'billing',
              label: 'Нэхэмжлэл үүсгэх',
              children: <BillingTab canEdit={level === 'edit'} />,
            },
          ]}
        />
      </Card>
    </LayoutWithMenu>
  );
};

export default ClubBilling;
